import React, { useEffect, useState } from "react";
import { Plus, Trash2, Pencil } from "lucide-react";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import api, { money, apiError } from "@/lib/api";
import { toast } from "sonner";

const emptyOffer = { title: "", description: "", image: "", type: "Meal Deals", original_price: 0, discounted_price: 0, expiry: "", active: true, pricing_mode: "per_item", product_ids: [] };
const emptyPromo = { code: "", type: "percent", value: 10, min_order: 0, usage_limit: 0, active: true };

export default function AdminOffers() {
  const [tab, setTab] = useState("offers");
  const [offers, setOffers] = useState([]);
  const [promos, setPromos] = useState([]);
  const [editOffer, setEditOffer] = useState(null);
  const [oForm, setOForm] = useState(emptyOffer);
  const [showPromo, setShowPromo] = useState(false);
  const [pForm, setPForm] = useState(emptyPromo);
  const [products, setProducts] = useState([]);
  const [prodSearch, setProdSearch] = useState("");

  const load = () => {
    api.get("/admin/offers").then(({ data }) => setOffers(data));
    api.get("/admin/promos").then(({ data }) => setPromos(data));
    api.get("/admin/products").then(({ data }) => setProducts(data));
  };
  useEffect(() => { load(); }, []);

  const saveOffer = async () => {
    const payload = { ...oForm, product_ids: oForm.product_ids || [], original_price: +oForm.original_price, discounted_price: +oForm.discounted_price };
    try {
      if (editOffer === "new") await api.post("/admin/offers", payload);
      else await api.put(`/admin/offers/${editOffer}`, payload);
      setEditOffer(null); load(); toast.success("Offer saved");
    } catch (e) { toast.error(apiError(e)); }
  };
  const toggleProduct = (id) => {
    const cur = oForm.product_ids || [];
    setOForm({ ...oForm, product_ids: cur.includes(id) ? cur.filter((x) => x !== id) : [...cur, id] });
  };
  const delOffer = async (id) => { await api.delete(`/admin/offers/${id}`); load(); };
  const savePromo = async () => {
    try { await api.post("/admin/promos", { ...pForm, value: +pForm.value, min_order: +pForm.min_order, usage_limit: +pForm.usage_limit }); setShowPromo(false); setPForm(emptyPromo); load(); toast.success("Promo created"); }
    catch (e) { toast.error(apiError(e)); }
  };
  const delPromo = async (code) => { await api.delete(`/admin/promos/${code}`); load(); };
  const togglePromo = async (p) => { await api.put(`/admin/promos/${p.code}`, { active: !p.active }); load(); };

  return (
    <div>
      <h1 className="font-heading font-extrabold text-3xl mb-4">Offers & Promos</h1>
      <div className="flex gap-2 mb-5">
        {["offers", "promos"].map((t) => <button key={t} onClick={() => setTab(t)} className={`px-4 py-2 rounded-full text-sm font-semibold capitalize ${tab === t ? "bg-metros-black text-white" : "bg-white border border-gray-200"}`}>{t}</button>)}
      </div>

      {tab === "offers" ? (
        <>
          <button data-testid="add-offer" onClick={() => { setOForm(emptyOffer); setProdSearch(""); setEditOffer("new"); }} className="mb-4 bg-metros-red text-white rounded-full px-5 py-2.5 text-sm font-bold flex items-center gap-1"><Plus size={16} /> Add Offer</button>
          <div className="grid md:grid-cols-2 gap-3">
            {offers.map((o) => (
              <div key={o.id} className="bg-white border border-gray-100 rounded-2xl p-3 flex gap-3">
                {o.image ? <img src={o.image} alt={o.title} className="h-16 w-16 rounded-xl object-cover" /> : <div className="h-16 w-16 rounded-xl bg-gray-100" />}
                <div className="flex-1"><p className="font-semibold text-sm">{o.title}</p><p className="text-xs text-muted-foreground">{o.type}</p><p className="text-sm font-bold text-metros-red">{money(o.discounted_price)}</p></div>
                <div className="flex flex-col gap-2">
                  <button onClick={() => { setOForm({ ...o, product_ids: o.product_ids || [] }); setProdSearch(""); setEditOffer(o.id); }} className="text-gray-400 hover:text-metros-black"><Pencil size={16} /></button>
                  <button onClick={() => delOffer(o.id)} className="text-gray-400 hover:text-metros-red"><Trash2 size={16} /></button>
                </div>
              </div>
            ))}
          </div>
        </>
      ) : (
        <>
          <button data-testid="add-promo" onClick={() => { setPForm(emptyPromo); setShowPromo(true); }} className="mb-4 bg-metros-red text-white rounded-full px-5 py-2.5 text-sm font-bold flex items-center gap-1"><Plus size={16} /> Add Promo</button>
          <div className="grid gap-2">
            {promos.map((p) => (
              <div key={p.code} className="bg-white border border-gray-100 rounded-xl p-3 flex items-center gap-3">
                <span className="font-heading font-bold bg-gray-100 rounded-lg px-3 py-1.5 text-sm">{p.code}</span>
                <span className="text-sm text-muted-foreground flex-1">{p.type === "percent" ? `${p.value}% off` : p.type === "fixed" ? `${money(p.value)} off` : "Free delivery"}{p.min_order > 0 ? ` · min ${money(p.min_order)}` : ""} · used {p.used_count}</span>
                <Switch checked={p.active} onCheckedChange={() => togglePromo(p)} />
                <button onClick={() => delPromo(p.code)} className="text-gray-400 hover:text-metros-red"><Trash2 size={16} /></button>
              </div>
            ))}
          </div>
        </>
      )}

      <Dialog open={!!editOffer} onOpenChange={(v) => !v && setEditOffer(null)}>
        <DialogContent className="max-w-md w-[95vw] bg-white rounded-3xl max-h-[90vh] overflow-y-auto">
          <h2 className="font-heading font-extrabold text-xl">{editOffer === "new" ? "Add Offer" : "Edit Offer"}</h2>
          <div className="space-y-2.5 mt-2">
            <input data-testid="of-title" placeholder="Title" value={oForm.title} onChange={(e) => setOForm({ ...oForm, title: e.target.value })} className="w-full rounded-xl border border-gray-200 px-4 py-2.5 text-sm" />
            <input placeholder="Description" value={oForm.description} onChange={(e) => setOForm({ ...oForm, description: e.target.value })} className="w-full rounded-xl border border-gray-200 px-4 py-2.5 text-sm" />
            <div className="space-y-2">
              <input placeholder="Image URL" value={oForm.image} onChange={(e) => setOForm({ ...oForm, image: e.target.value })} className="w-full rounded-xl border border-gray-200 px-4 py-2.5 text-sm" data-testid="offer-image-url" />
              <div className="flex items-center gap-3">
                <label data-testid="offer-image-upload" className="inline-flex items-center gap-2 cursor-pointer bg-metros-black text-white rounded-full px-4 py-2 text-xs font-semibold hover:bg-black/80">
                  Upload image
                  <input type="file" accept="image/*" className="hidden" onChange={(e) => {
                    const file = e.target.files?.[0]; if (!file) return;
                    const reader = new FileReader();
                    reader.onload = () => {
                      const img = new window.Image();
                      img.onload = () => {
                        const MAX = 1000; let w = img.width, h = img.height;
                        if (w > MAX || h > MAX) { const r = Math.min(MAX / w, MAX / h); w = Math.round(w * r); h = Math.round(h * r); }
                        const canvas = document.createElement("canvas"); canvas.width = w; canvas.height = h;
                        canvas.getContext("2d").drawImage(img, 0, 0, w, h);
                        setOForm((f) => ({ ...f, image: canvas.toDataURL("image/jpeg", 0.82) }));
                      };
                      img.onerror = () => setOForm((f) => ({ ...f, image: reader.result }));
                      img.src = reader.result;
                    };
                    reader.readAsDataURL(file);
                  }} />
                </label>
                {oForm.image && <img src={oForm.image} alt="preview" className="h-12 w-12 rounded-lg object-cover border border-gray-200" data-testid="offer-image-preview" />}
              </div>
            </div>
            <input placeholder="Type (e.g. Meal Deals)" value={oForm.type} onChange={(e) => setOForm({ ...oForm, type: e.target.value })} className="w-full rounded-xl border border-gray-200 px-4 py-2.5 text-sm" />
            <div className="flex gap-2">
              <input type="number" step="0.01" placeholder="Original £" value={oForm.original_price} onChange={(e) => setOForm({ ...oForm, original_price: e.target.value })} className="flex-1 rounded-xl border border-gray-200 px-4 py-2.5 text-sm" />
              <input type="number" step="0.01" placeholder="Discounted £" value={oForm.discounted_price} onChange={(e) => setOForm({ ...oForm, discounted_price: e.target.value })} className="flex-1 rounded-xl border border-gray-200 px-4 py-2.5 text-sm" />
            </div>
            <select data-testid="of-pricing-mode" value={oForm.pricing_mode || "per_item"} onChange={(e) => setOForm({ ...oForm, pricing_mode: e.target.value })} className="w-full rounded-xl border border-gray-200 px-4 py-2.5 text-sm">
              <option value="per_item">Per-item price — each selected item sold at the discounted price</option>
              <option value="bundle">Bundle price — all selected items together cost the discounted price</option>
            </select>
            <div className="pt-1">
              <p className="text-xs font-semibold text-gray-700 mb-1">Included products <span className="text-gray-400 font-normal">({(oForm.product_ids || []).length} selected)</span></p>
              <p className="text-[11px] text-gray-400 mb-2">Pick the exact items in this deal. Leave empty to fall back to category "{oForm.type}".</p>
              <input data-testid="of-prod-search" placeholder="Search products…" value={prodSearch} onChange={(e) => setProdSearch(e.target.value)} className="w-full rounded-xl border border-gray-200 px-4 py-2 text-sm mb-2" />
              <div className="max-h-48 overflow-y-auto border border-gray-100 rounded-xl divide-y divide-gray-50">
                {products.filter((p) => !prodSearch || p.name.toLowerCase().includes(prodSearch.toLowerCase())).map((p) => {
                  const checked = (oForm.product_ids || []).includes(p.id);
                  return (
                    <label key={p.id} data-testid={`of-prod-${p.id}`} className={`flex items-center gap-2 px-3 py-2 text-sm cursor-pointer ${checked ? "bg-red-50" : "hover:bg-gray-50"}`}>
                      <input type="checkbox" checked={checked} onChange={() => toggleProduct(p.id)} className="accent-metros-red" />
                      <span className="flex-1 truncate">{p.name}</span>
                      <span className="text-xs text-gray-400">{p.branch_id}</span>
                      <span className="text-xs font-semibold">{money(p.price)}</span>
                    </label>
                  );
                })}
                {products.length === 0 && <p className="px-3 py-4 text-xs text-gray-400 text-center">No products available.</p>}
              </div>
            </div>
          </div>
          <button data-testid="save-offer" onClick={saveOffer} className="mt-4 w-full bg-metros-red text-white rounded-full py-2.5 font-bold text-sm">Save</button>
        </DialogContent>
      </Dialog>

      <Dialog open={showPromo} onOpenChange={setShowPromo}>
        <DialogContent className="max-w-md w-[95vw] bg-white rounded-3xl">
          <h2 className="font-heading font-extrabold text-xl">Add Promo Code</h2>
          <div className="space-y-2.5 mt-2">
            <input data-testid="pf-code" placeholder="CODE" value={pForm.code} onChange={(e) => setPForm({ ...pForm, code: e.target.value.toUpperCase() })} className="w-full rounded-xl border border-gray-200 px-4 py-2.5 text-sm uppercase" />
            <select value={pForm.type} onChange={(e) => setPForm({ ...pForm, type: e.target.value })} className="w-full rounded-xl border border-gray-200 px-4 py-2.5 text-sm">
              <option value="percent">Percentage discount</option><option value="fixed">Fixed discount</option><option value="free_delivery">Free delivery</option>
            </select>
            {pForm.type !== "free_delivery" && <input type="number" placeholder="Value" value={pForm.value} onChange={(e) => setPForm({ ...pForm, value: e.target.value })} className="w-full rounded-xl border border-gray-200 px-4 py-2.5 text-sm" />}
            <input type="number" placeholder="Min order £" value={pForm.min_order} onChange={(e) => setPForm({ ...pForm, min_order: e.target.value })} className="w-full rounded-xl border border-gray-200 px-4 py-2.5 text-sm" />
            <input type="number" placeholder="Usage limit (0 = unlimited)" value={pForm.usage_limit} onChange={(e) => setPForm({ ...pForm, usage_limit: e.target.value })} className="w-full rounded-xl border border-gray-200 px-4 py-2.5 text-sm" />
          </div>
          <button data-testid="save-promo" onClick={savePromo} className="mt-4 w-full bg-metros-red text-white rounded-full py-2.5 font-bold text-sm">Create</button>
        </DialogContent>
      </Dialog>
    </div>
  );
}
